import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import SaveIcon from "@mui/icons-material/Save";
import { useNavigate } from "react-router-dom";

import { ContextSession } from "../../services/ContextSession";

const API_FALLBACK = "https://bei360.injupemp.gob.hn:8444/";
const API_BASE =
  (process.env.REACT_APP_BEI360_URL || API_FALLBACK).replace(/\/$/, "") + "/";

const COLUMNS = [
  { key: "CDREG", label: "CDREG" },
  { key: "CDRP", label: "CDRP" },
  { key: "RAMO", label: "RAMO" },
  { key: "PRG", label: "PRG" },
  { key: "NUMPLA", label: "NUMPLA" },
  { key: "SUEASG", label: "SUEASG" },
  { key: "DEDPLA", label: "DEPLA" },
  { key: "FECMOV", label: "FECMOV" },
  { key: "TIPPLA", label: "TIPPLA" },
];

const MONEY_COLUMNS = new Set([
  "SUEASG",
  "SUENET",
  "CUOPP",
  "CUOPH",
  "CUOREI",
  "DEDOTR",
  "DEDPLA",
  "CUOPPT",
  "DEDOT2",
]);

function formatMoney(value) {
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return value ?? "";
  return numberValue.toLocaleString("es-HN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCell(column, value) {
  if (MONEY_COLUMNS.has(column)) return formatMoney(value);
  return value ?? "";
}

function normalizeRows(payload) {
  const list = Array.isArray(payload?.results)
    ? payload.results
    : Array.isArray(payload)
      ? payload
      : [];

  return list.map((row, index) => ({
    ...row,
    __rowId: row.id || `${row.CDRP || "ROW"}-${index + 1}-${Date.now()}`,
    estadoRegistro: row.estadoRegistro || "PENDIENTE",
  }));
}

function buildError(response, data, fallback) {
  const error = new Error(
    data?.detail || data?.error || data?.message || fallback,
  );
  error.status = response.status;
  error.code = data?.code;
  return error;
}

async function requestJson(url, { token, body }) {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body || {}),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw buildError(response, data, "No se pudo completar la solicitud.");
  }

  return data;
}

export default function DesglosePlanillas() {
  const sessi = React.useContext(ContextSession);
  const navigate = useNavigate();

  const [cantidad, setCantidad] = React.useState(12);
  const [rows, setRows] = React.useState([]);
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [loadingLoad, setLoadingLoad] = React.useState(false);
  const [loadingSave, setLoadingSave] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [successMsg, setSuccessMsg] = React.useState("");
  const [dryRun, setDryRun] = React.useState(false);
  const [lastMeta, setLastMeta] = React.useState(null);

  const token = sessi?.getToken ? sessi.getToken() : sessi?.sessionData?.token;

  const selectedRows = React.useMemo(
    () => rows.filter((row) => selectedIds.has(row.__rowId)),
    [rows, selectedIds],
  );

  const allSelected = rows.length > 0 && selectedIds.size === rows.length;

  const handleAuthError = (error) => {
    if (error?.status === 401 && error?.code === "token_not_valid") {
      sessi.clearSession();
      navigate("/");
      return true;
    }
    return false;
  };

  const handleLoad = async () => {
    setLoadingLoad(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const data = await requestJson(`${API_BASE}api/Planillas/desglose/cargar-as400/`, {
        token,
        body: { cantidad },
      });
      const nextRows = normalizeRows(data);
      setRows(nextRows);
      setSelectedIds(new Set(nextRows.map((row) => row.__rowId)));
      setLastMeta({
        source: data?.source || "AS400",
        table: data?.table || "FB.DEDPLAF1D",
        count: nextRows.length,
      });
      setSuccessMsg(data?.message || "Desglose cargado correctamente.");
    } catch (error) {
      if (!handleAuthError(error)) {
        setErrorMsg(error.message || "No se pudo cargar el desglose.");
      }
    } finally {
      setLoadingLoad(false);
    }
  };

  const handleRegister = async () => {
    if (!selectedRows.length) {
      setErrorMsg("Seleccione al menos un registro.");
      return;
    }

    setLoadingSave(true);
    setErrorMsg("");
    setSuccessMsg("");

    const registros = selectedRows.map(({ __rowId, estadoRegistro, id, ...record }) => record);

    try {
      const data = await requestJson(`${API_BASE}api/Planillas/historico/registrar-as400/`, {
        token,
        body: { registros, dry_run: dryRun },
      });

      if (!dryRun) {
        setRows((currentRows) =>
          currentRows.map((row) =>
            selectedIds.has(row.__rowId)
              ? { ...row, estadoRegistro: "REGISTRADO" }
              : row,
          ),
        );
        setSelectedIds(new Set());
      }

      setSuccessMsg(data?.message || "Registros procesados correctamente.");
    } catch (error) {
      if (!handleAuthError(error)) {
        setErrorMsg(error.message || "No se pudo registrar el historico.");
      }
    } finally {
      setLoadingSave(false);
    }
  };

  const handleToggleAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(new Set(rows.map((row) => row.__rowId)));
      return;
    }
    setSelectedIds(new Set());
  };

  const handleToggleRow = (rowId) => (event) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (event.target.checked) {
        next.add(rowId);
      } else {
        next.delete(rowId);
      }
      return next;
    });
  };

  const handleClear = () => {
    setRows([]);
    setSelectedIds(new Set());
    setErrorMsg("");
    setSuccessMsg("");
    setLastMeta(null);
  };

  return (
    <Box sx={{ px: { xs: 1, md: 3 }, py: { xs: 2, md: 3 }, width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
      <Stack spacing={2.5}>
        <Stack
          direction="column"
          alignItems="stretch"
          spacing={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={800}>
              Planillas
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mt: 0.5 }}>
              Desglose historico
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            justifyContent="flex-end"
            flexWrap="wrap"
            useFlexGap
            sx={{ maxWidth: "100%" }}
          >
            <TextField
              label="Cantidad"
              type="number"
              size="small"
              value={cantidad}
              onChange={(event) => {
                const value = Number(event.target.value);
                setCantidad(Number.isNaN(value) ? 1 : Math.max(1, Math.min(value, 100)));
              }}
              inputProps={{ min: 1, max: 100 }}
              sx={{ width: { xs: "100%", sm: 120 } }}
            />
            <Tooltip title="Cargar Desglose AS400">
              <span>
                <Button
                  variant="contained"
                  startIcon={
                    loadingLoad ? <CircularProgress size={18} color="inherit" /> : <CloudSyncIcon />
                  }
                  onClick={handleLoad}
                  disabled={loadingLoad || loadingSave}
                >
                  Cargar Desglose AS400
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Registrar Historico">
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={
                    loadingSave ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
                  }
                  onClick={handleRegister}
                  disabled={loadingSave || loadingLoad || selectedRows.length === 0}
                >
                  Registrar Historico
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Limpiar">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<DeleteSweepIcon />}
                  onClick={handleClear}
                  disabled={loadingLoad || loadingSave || rows.length === 0}
                >
                  Limpiar
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`Registros: ${rows.length}`} />
              <Chip label={`Seleccionados: ${selectedRows.length}`} variant="outlined" />
              <Chip label={lastMeta?.table || "FB.DEDPLAF1D"} color="primary" variant="outlined" />
              {lastMeta?.source ? <Chip label={lastMeta.source} color="secondary" /> : null}
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={dryRun}
                  onChange={(event) => setDryRun(event.target.checked)}
                />
              }
              label="Solo validar"
            />
          </Stack>
        </Paper>

        {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}
        {successMsg ? <Alert severity="success">{successMsg}</Alert> : null}

        <Paper elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Registros de desglose
            </Typography>
          </Box>
          <Divider />

          <TableContainer sx={{ maxHeight: "65vh", overflowX: "auto" }}>
            <Table stickyHeader size="small" sx={{ minWidth: 980, tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 48 }}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedIds.size > 0 && !allSelected}
                      onChange={handleToggleAll}
                      disabled={!rows.length}
                    />
                  </TableCell>
                  {COLUMNS.map((column) => (
                    <TableCell
                      key={column.key}
                      align={MONEY_COLUMNS.has(column.key) ? "right" : "left"}
                      sx={{ width: MONEY_COLUMNS.has(column.key) ? 120 : 92, fontWeight: 700 }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length + 1} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">Sin registros cargados</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={row.__rowId}
                      hover
                      selected={selectedIds.has(row.__rowId)}
                      sx={{ "& td": { whiteSpace: "nowrap" } }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(row.__rowId)}
                          onChange={handleToggleRow(row.__rowId)}
                          disabled={row.estadoRegistro === "REGISTRADO"}
                        />
                      </TableCell>
                      {COLUMNS.map((column) => (
                        <TableCell
                          key={`${row.__rowId}-${column.key}`}
                          align={MONEY_COLUMNS.has(column.key) ? "right" : "left"}
                        >
                          {formatCell(column.key, row[column.key])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    </Box>
  );
}
